/**
 * PowerLogTable Component
 * Desktop table view for power log entries
 */

import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatCurrencyWithSymbol, formatDate } from '../utils/formatters';
import ElectronicPowerBadge from '../../../shared/ElectronicPowerBadge';

const PowerLogTable = ({ logs, onEdit, onDelete, agencyId }) => {
  return (
    <div>
      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th scope="col" className="w-[10%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Power #
            </th>
            <th scope="col" className="w-[9%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Date
            </th>
            <th scope="col" className="w-[18%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Defendant
            </th>
            <th scope="col" className="w-[10%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Case #
            </th>
            <th scope="col" className="w-[10%] px-2 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Bond
            </th>
            <th scope="col" className="w-[9%] px-2 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Premium
            </th>
            <th scope="col" className="w-[12%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Disposition
            </th>
            <th scope="col" className="w-[14%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Agent
            </th>
            <th scope="col" className="w-[8%] px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {logs.map((log) => (
            <tr
              key={log.logId}
              className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <td className="px-2 py-3">
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {log.powerNo}
                  </span>
                  <ElectronicPowerBadge power={log} powerNumber={log.powerNo} agencyId={agencyId} size="xs" />
                </span>
              </td>
              <td className="px-2 py-3">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {log.dateExecuted ? formatDate(log.dateExecuted) : '-'}
                </span>
              </td>
              <td className="px-2 py-3">
                <span className="text-sm text-gray-900 dark:text-white truncate block" title={log.name || ''}>
                  {log.name || '-'}
                </span>
              </td>
              <td className="px-2 py-3">
                <span className="text-sm text-gray-600 dark:text-gray-400 truncate block" title={log.caseNumber || ''}>
                  {log.caseNumber || '-'}
                </span>
              </td>
              <td className="px-2 py-3 text-right">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {log.amountOfBond != null ? formatCurrencyWithSymbol(log.amountOfBond) : '-'}
                </span>
              </td>
              <td className="px-2 py-3 text-right">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {log.premiumCharged != null ? formatCurrencyWithSymbol(log.premiumCharged) : '-'}
                </span>
              </td>
              <td className="px-2 py-3">
                <div className="text-sm text-gray-600 dark:text-gray-400 truncate" title={log.disposition || ''}>
                  {log.disposition || log.dispositionDate ? (
                    <>
                      {log.disposition && <div className="truncate">{log.disposition}</div>}
                      {log.dispositionDate && (
                        <div className={log.disposition ? "text-xs text-gray-500" : ""}>
                          {formatDate(log.dispositionDate)}
                        </div>
                      )}
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              </td>
              <td className="px-2 py-3">
                <span className="text-sm text-gray-600 dark:text-gray-400 truncate block" title={log.agentsName || ''}>
                  {log.agentsName || '-'}
                </span>
              </td>
              <td className="px-2 py-3 text-center">
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={() => onEdit(log)}
                    className="p-1 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded transition-colors"
                    title="Edit entry"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(log)}
                    className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                    title="Delete entry"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PowerLogTable;
